import React, { useState } from "react";

interface IpcAudit { id: string; date: string; auditor: string; hand: string; ppe: string; decon: string; env: string; waste: string; linen: string; }
interface Infection { id: string; date: string; residentName: string; type: string; outcome: string; notes: string; }
interface Outbreak { id: string; startDate: string; pathogen: string; cases: number; cqcNotified: boolean; cqcDate: string; hptNotified: boolean; hptDate: string; actions: string; standDown: { label: string; done: boolean }[]; closedDate: string; }

const AUDITS_KEY = "ipc_audits";
const INF_KEY = "ipc_infections";
const OUT_KEY = "ipc_outbreaks";

const AUDIT_ITEMS = ["hand", "ppe", "decon", "env", "waste", "linen"] as const;
const AUDIT_LABELS: Record<string, string> = { hand: "Hand hygiene", ppe: "PPE use & stock", decon: "Equipment decontamination", env: "Environment cleanliness", waste: "Waste management", linen: "Linen handling" };
const INFECTION_TYPES = ["Respiratory", "Gastrointestinal", "UTI", "Skin / wound", "Influenza-like", "Other"];
const STAND_DOWN = ["No new cases for the required exclusion period", "Terminal / deep clean completed", "All cases symptom-free for 48 hours", "IPC lead sign-off obtained"];

const load = <T,>(key: string): T[] => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };
const saveKey = (key: string, data: any[]) => localStorage.setItem(key, JSON.stringify(data));
const today = () => new Date().toISOString().slice(0, 10);
const hoursSince = (d: string) => (Date.now() - new Date(d).getTime()) / 36400000;

export default function Ipc({ user }: { user?: any }) {
  const [tab, setTab] = useState<"audits" | "surveillance" | "outbreak">("audits");
  const [audits, setAudits] = useState<IpcAudit[]>(() => load(AUDITS_KEY));
  const [infections, setInfections] = useState<Infection[]>(() => load(INF_KEY));
  const [outbreaks, setOutbreaks] = useState<Outbreak[]>(() => load(OUT_KEY));
  const residents = load("residents_records");
  const [message, setMessage] = useState("");

  const [auditForm, setAuditForm] = useState<Partial<IpcAudit>>({ date: today(), hand: "Pass", ppe: "Pass", decon: "Pass", env: "Pass", waste: "Pass", linen: "Pass", auditor: user && user.name ? user.name : "" });
  const [infForm, setInfForm] = useState<Partial<Infection>>({ date: today(), type: "Respiratory" });
  const [outForm, setOutForm] = useState<Partial<Outbreak>>({ startDate: today(), pathogen: "" });

  const say = (m: string) => { setMessage(m); setTimeout(() => setMessage(""), 4000); };

  const saveAudit = () => {
    const rec: IpcAudit = { id: `ipca-${Date.now()}`, date: auditForm.date || today(), auditor: auditForm.auditor || "", hand: auditForm.hand || "Pass", ppe: auditForm.ppe || "Pass", decon: auditForm.decon || "Pass", env: auditForm.env || "Pass", waste: auditForm.waste || "Pass", linen: auditForm.linen || "Pass" };
    const next = [rec, ...audits]; setAudits(next); saveKey(AUDITS_KEY, next);
    say("IPC audit saved.");
  };

  const saveInfection = () => {
    if (!infForm.residentName) { say("Resident is required for the line listing."); return; }
    const rec: Infection = { id: `infc-${Date.now()}`, date: infForm.date || today(), residentName: infForm.residentName || "", type: infForm.type || "Other", outcome: infForm.outcome || "", notes: infForm.notes || "" };
    const next = [rec, ...infections]; setInfections(next); saveKey(INF_KEY, next);
    setInfForm({ date: today(), type: "Respiratory" });
    say("Infection added to surveillance line listing.");
  };

  const startOutbreak = () => {
    const rec: Outbreak = { id: `out-${Date.now()}`, startDate: outForm.startDate || today(), pathogen: outForm.pathogen || "Suspected - awaiting confirmation", cases: parseInt(String(outForm.cases)) || 1, cqcNotified: false, cqcDate: "", hptNotified: false, hptDate: "", actions: "", standDown: STAND_DOWN.map(label => ({ label, done: false })), closedDate: "" };
    const next = [rec, ...outbreaks]; setOutbreaks(next); saveKey(OUT_KEY, next);
    say("OUTBREAK OPENED - CQC notification due within 24 hours and UKHSA Health Protection Team must be informed.");
  };

  const updateOutbreak = (id: string, patch: Partial<Outbreak>) => {
    const next = outbreaks.map(o => (o.id === id ? { ...o, ...patch } : o));
    setOutbreaks(next); saveKey(OUT_KEY, next);
  };

  const toggleStandDown = (o: Outbreak, label: string) => {
    updateOutbreak(o.id, { standDown: o.standDown.map(s => (s.label === label ? { ...s, done: !s.done } : s)) });
  };

  const auditScore = (a: IpcAudit) => Math.round((AUDIT_ITEMS.filter(k => a[k] === "Pass").length / AUDIT_ITEMS.length) * 100);

  const lastMonths = (n: number) => { const out: string[] = []; const d = new Date(); for (let i = n - 1; i >= 0; i--) { const dt = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`); } return out; };
  const infByMonth = lastMonths(6).map(m => infections.filter(i => (i.date || "").slice(0, 7) === m).length);
  const maxInf = Math.max(1, ...infByMonth);

  const openOutbreaks = outbreaks.filter(o => !o.closedDate);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">IPC & Outbreak Command</h2>
        <p className="text-sm text-slate-600">Infection prevention audits, surveillance line-listing with trend, and outbreak management with statutory notification deadlines (CQC 24h + UKHSA HPT).</p>
      </div>

      {openOutbreaks.some(o => hoursSince(o.startDate) > 24 && (!o.cqcNotified || !o.hptNotified)) ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">STATUTORY BREACH RISK: an open outbreak is past 24 hours without both CQC and Health Protection Team notification. Act now.</div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(["audits", "surveillance", "outbreak"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "audits" ? "IPC Audits" : t === "surveillance" ? "Surveillance" : "Outbreak Command"}</button>
        ))}
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "audits" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">New IPC audit</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <input type="date" value={auditForm.date || ""} onChange={e => setAuditForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={auditForm.auditor || ""} onChange={e => setAuditForm(f => ({ ...f, auditor: e.target.value }))} placeholder="Auditor" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              {AUDIT_ITEMS.map(k => (
                <div key={k}>
                  <label className="mb-1 block text-xs font-medium text-slate-600">{AUDIT_LABELS[k]}</label>
                  <select value={(auditForm as any)[k]} onChange={e => setAuditForm(f => ({ ...f, [k]: e.target.value }))} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                    <option>Pass</option><option>Fail</option>
                  </select>
                </div>
              ))}
            </div>
            <button onClick={saveAudit} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save IPC audit</button>
          </div>
          <div className="space-y-2">
            {audits.map(a => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm text-slate-800">{a.date} | {a.auditor} | {AUDIT_ITEMS.map(k => `${AUDIT_LABELS[k]}: ${a[k]}`).join(" | ")}</div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${auditScore(a) >= 90 ? "bg-green-100 text-green-700" : auditScore(a) >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{auditScore(a)}%</span>
              </div>
            ))}
            {audits.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No IPC audits yet - run one monthly.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "surveillance" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Add to line listing</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <input type="date" value={infForm.date || ""} onChange={e => setInfForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <select value={infForm.residentName || ""} onChange={e => setInfForm(f => ({ ...f, residentName: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
                <option value="">Resident</option>
                {residents.map((r: any) => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
              <select value={infForm.type || "Respiratory"} onChange={e => setInfForm(f => ({ ...f, type: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
                {INFECTION_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <input value={infForm.outcome || ""} onChange={e => setInfForm(f => ({ ...f, outcome: e.target.value }))} placeholder="Outcome (recovering / resolved / ...)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={infForm.notes || ""} onChange={e => setInfForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <button onClick={saveInfection} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add infection</button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Six-month infection trend</h3>
            <div className="flex h-32 items-end gap-2">
              {lastMonths(6).map((m, i) => (
                <div key={m} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-xs text-slate-600">{infByMonth[i] || ""}</div>
                  <div className="w-full rounded-t" style={{ height: `${(infByMonth[i] / maxInf) * 100}%`, minHeight: infByMonth[i] ? "6px" : "2px", background: "#7c3aed" }} />
                  <div className="text-xs text-slate-500">{new Date(m + "-01").toLocaleDateString(undefined, { month: "short" })}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {infections.map(i => (
              <div key={i.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="text-sm text-slate-800">{i.date} | {i.residentName} | {i.type} | {i.outcome || "ongoing"}</div>
              </div>
            ))}
            {infections.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No infections recorded.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "outbreak" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Open an outbreak record</h3>
            <div className="flex flex-wrap gap-2">
              <input type="date" value={outForm.startDate || ""} onChange={e => setOutForm(f => ({ ...f, startDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={outForm.pathogen || ""} onChange={e => setOutForm(f => ({ ...f, pathogen: e.target.value }))} placeholder="Pathogen / setting (e.g. Norovirus - 1st floor)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="number" value={outForm.cases ?? ""} onChange={e => setOutForm(f => ({ ...f, cases: e.target.value as any }))} placeholder="Cases" className="w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <button onClick={startOutbreak} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Open outbreak</button>
            </div>
          </div>
          {outbreaks.map(o => (
            <div key={o.id} className="rounded-xl border-2 border-red-300 bg-red-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-semibold text-red-800">{o.pathogen} - opened {o.startDate} | {o.cases} case(s){o.closedDate ? ` | CLOSED ${o.closedDate}` : " | OPEN"}</div>
                {!o.closedDate ? <button onClick={() => updateOutbreak(o.id, { closedDate: today() })} className="rounded-lg bg-green-700 px-3 py-1 text-sm text-white">Close outbreak</button> : null}
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={o.cqcNotified} onChange={e => updateOutbreak(o.id, { cqcNotified: e.target.checked, cqcDate: e.target.checked ? today() : "" })} />CQC notified (due within 24h){o.cqcDate ? ` - ${o.cqcDate}` : ""}</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={o.hptNotified} onChange={e => updateOutbreak(o.id, { hptNotified: e.target.checked, hptDate: e.target.checked ? today() : "" })} />UKHSA Health Protection Team notified{o.hptDate ? ` - ${o.hptDate}` : ""}</label>
              </div>
              {hoursSince(o.startDate) > 24 && (!o.cqcNotified || !o.hptNotified) && !o.closedDate ? <div className="mt-2 text-sm font-semibold text-red-700">NOTIFICATION OVERDUE - statutory breach risk.</div> : null}
              <textarea value={o.actions} onChange={e => updateOutbreak(o.id, { actions: e.target.value })} rows={2} placeholder="Control actions (isolation, cohorting, PPE, visitor restrictions, deep clean...)" className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <div className="mt-2 space-y-1">
                {o.standDown.map(s => (
                  <label key={s.label} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.done} onChange={() => toggleStandDown(o, s.label)} />{s.label}</label>
                ))}
              </div>
              {o.standDown.every(s => s.done) ? <div className="mt-2 text-sm font-semibold text-green-700">All stand-down criteria met - safe to close.</div> : null}
            </div>
          ))}
          {outbreaks.length === 0 ? <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700">No outbreaks recorded. Long may it continue.</div> : null}
        </div>
      ) : null}
    </div>
  );
}